/**
 * Seamless theme-version upgrade — WITHOUT interrupting the current page.
 *
 * The problem: right after we publish a new theme, Shopify's edge/full-page cache
 * can keep handing already-open (or freshly-opened-but-cached) tabs an OLD version.
 *
 * The old approach force-reloaded the stale page. That feels like spam — the page
 * the visitor is looking at suddenly jumps and reloads under them.
 *
 * This approach never reloads the current page. Instead it:
 *   1. Silently checks, in the background, whether a newer theme has been published.
 *   2. If so, it quietly flags "an upgrade is pending" — but does nothing visible.
 *   3. The next time the visitor CLICKS an internal link, it appends a one-time
 *      cache-buster (?_v=<latest-id>) to that destination so the next page is served
 *      fresh — i.e. the latest published version — instead of a stale cached copy.
 *
 * So the visitor's initial experience is untouched, and they seamlessly roll onto the
 * latest version as they browse. Once they land on an up-to-date page, the pending
 * flag clears and navigation goes back to normal (cache-friendly) links.
 *
 * layout/theme.liquid stamps every page with:
 *   <meta name="theme-version-id" content="{{ theme.id }}">
 */
(function () {
  var versionMeta = document.querySelector('meta[name="theme-version-id"]');
  var CURRENT_VERSION = versionMeta ? versionMeta.content : null;
  if (!CURRENT_VERSION) return;

  var latestVersion = CURRENT_VERSION; // best-known latest published id
  var needsUpgrade = false;            // true once a newer version is detected

  var CHECK_INTERVAL_MS = 60 * 1000;   // re-check quietly every minute while visible
  var MIN_TIME_BETWEEN_CHECKS_MS = 20 * 1000;
  var lastCheck = 0;

  // --- silent background check: has a newer theme been published? -------------
  function checkLatest() {
    lastCheck = Date.now();
    // Unique param so Shopify's edge cache can't hand this probe the same stale HTML.
    var sep = location.search ? '&' : '?';
    fetch(location.pathname + location.search + sep + '_vck=' + Date.now(), {
      cache: 'no-store',
      credentials: 'same-origin',
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var m = html.match(/<meta name="theme-version-id" content="([^"]+)"/);
        if (!m || !m[1]) return;
        if (m[1] !== CURRENT_VERSION) {
          latestVersion = m[1];
          needsUpgrade = true; // no reload — we upgrade on the next navigation
        } else {
          needsUpgrade = false;
        }
      })
      .catch(function () { /* network hiccup — try again next tick */ });
  }

  // --- upgrade on the next click (never reload the current page) --------------
  function isSameOrigin(url) {
    try { return new URL(url, location.href).origin === location.origin; }
    catch (e) { return false; }
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!needsUpgrade) return;
      // Respect the browser's own gestures (new tab, download, etc.).
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;

      var raw = a.getAttribute('href');
      if (!raw) return;
      if (raw.charAt(0) === '#') return;                        // same-page anchor
      if (/^(mailto:|tel:|javascript:)/i.test(raw)) return;     // non-navigations
      if (a.hasAttribute('download')) return;
      if (!isSameOrigin(a.href)) return;                        // external link
      if (/[?&]_v=/.test(a.href)) return;                       // already busted

      // Rewrite the destination so the NEXT page loads the latest published version.
      // Done in the capture phase, before the browser reads href for navigation.
      try {
        var u = new URL(a.href, location.href);
        u.searchParams.set('_v', latestVersion);
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (err) { /* leave the link untouched on any parse error */ }
    },
    true // capture: run before default navigation
  );

  // --- when to check (all silent; none of these reload the page) --------------
  setInterval(function () {
    if (document.visibilityState === 'visible') checkLatest();
  }, CHECK_INTERVAL_MS);

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && Date.now() - lastCheck > MIN_TIME_BETWEEN_CHECKS_MS) {
      checkLatest();
    }
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) checkLatest();
  });

  // One quiet check shortly after load so the pending flag is ready before the
  // visitor's first click — without touching their initial view.
  setTimeout(checkLatest, 1200);
})();
